import { DEFAULT_PAGE_SIZE, EVENT_SEVERITY, eventCodeSchema, formatParisDateTime, statusTone, type DashboardAlert, type DashboardAlertGroup } from '@flux/shared';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { Badge } from '@/components/ui/badge.js';
import { EmptyState } from '@/components/ui/empty-state.js';

/**
 * Alertes sur 24 h : le nombre par type montre immédiatement les problèmes
 * récurrents ; sa teinte distingue l'avertissement (orange) du critique (rouge).
 * Quand toutes les occurrences visent une commande ou une facture, le lien
 * ouvre la liste Commandes filtrée — plus lisible pour la logistique que le
 * Journal, réservé aux autres alertes (fichiers, runs, système).
 */
export function AlertsList({ alerts, groups }: { alerts: readonly DashboardAlert[]; groups?: readonly DashboardAlertGroup[] | undefined }): JSX.Element {
  const displayed = groups ?? [];
  const from = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  const to = new Date().toISOString();
  return (
    <PageSection title="Alertes des dernières 24 h" flush actions={
      <Link to="/journal" search={{ view: 'alertes', page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' }}
        className="text-sm font-medium text-primary hover:underline">Voir toutes les alertes</Link>
    }>
      {displayed.length === 0 && alerts.length === 0 ? (
        <EmptyState title="Aucune alerte" description="Aucun avertissement ni erreur depuis 24 h." className="py-10" />
      ) : (
        <ul className="divide-y divide-border">
          {(displayed.length > 0 ? displayed : alerts.map((alert) => ({ code: '', severity: alert.severity, titleFr: alert.titleFr, count: 1, lastAt: alert.at, ordersListable: false }))).map((group, index) => {
            const code = eventCodeSchema.safeParse(group.code);
            const linkClassName = 'flex items-center justify-between gap-3 rounded-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
            const linkContent = (
              <>
                <span className="text-sm font-medium">{group.titleFr}</span>
                <Badge tone={statusTone(EVENT_SEVERITY, group.severity)} className="h-auto border-transparent px-2 py-1 text-sm font-semibold">
                  {group.count}
                </Badge>
              </>
            );
            return (
              <li key={`${group.code}-${String(index)}`} className="px-5 py-3">
                {code.success && group.ordersListable === true ? (
                  <Link to="/commandes" search={{ alertCode: code.data, alertFrom: from, alertTo: to, page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' }}
                    className={linkClassName}>
                    {linkContent}
                  </Link>
                ) : code.success ? (
                  <Link to="/journal" search={{ view: 'alertes', code: [code.data], from, to, page: 1, pageSize: DEFAULT_PAGE_SIZE, sortDir: 'desc' }}
                    className={linkClassName}>
                    {linkContent}
                  </Link>
                ) : <div className="flex justify-between gap-3 text-sm"><span>{group.titleFr}</span><span>{group.count}</span></div>}
                <p className="mt-1 text-xs text-muted-foreground">Dernière occurrence : {formatParisDateTime(new Date(group.lastAt))}</p>
              </li>
            );
          })}
        </ul>
      )}
    </PageSection>
  );
}
